import { Component, Input, OnInit, SimpleChanges } from "@angular/core";
import { PageEvent } from "@angular/material/paginator";
import { Icon, Map as LeafletMap, icon, tileLayer, marker, latLngBounds, latLng, point } from "leaflet";
import { LF010DevicePeriod, ProjectDeviceModel } from "app/models/projectDevice.model";
import { Cloudinary } from "@cloudinary/url-gen";
import { CloudinaryImage } from "@cloudinary/url-gen/assets/CloudinaryImage";
import { fill, limitFit } from "@cloudinary/url-gen/actions/resize";

@Component({
    selector: "app-device-info",
    templateUrl: "./device-info.component.html",
    styleUrls: ["./device-info.component.scss"],
    standalone: false,
})
export class DeviceInfoComponent implements OnInit {
    private readonly defaultLF010DevicePeriods: LF010DevicePeriod[] = [
        { devicePeriod: 1, name: "Day" },
        { devicePeriod: 2, name: "Evening" },
        { devicePeriod: 3, name: "Night" },
    ];
    private map: LeafletMap | null = null;
    private readonly locationMarkerIcon: Icon = icon({
        iconUrl: "assets/blue-marker-noise-sensor.png",
        iconRetinaUrl: "assets/blue-marker-noise-sensor.png",
        iconSize: [50, 69],
        iconAnchor: [25, 69],
        popupAnchor: [0, -69],
        shadowUrl: undefined,
        className: "device-location-marker",
    });

    private readonly cloudinary = new Cloudinary({ cloud: { cloudName: "emmadb" } });
    private readonly cloudinaryImageCache = new Map<string, CloudinaryImage>();

    @Input()
    device: ProjectDeviceModel = {} as ProjectDeviceModel;

    readonly lf010HistoryColumns = ["period", "previousLimit", "newLimit", "changedAt"];
    readonly lf010HistoryPageSizeOptions = [5, 10, 25];
    lf010HistoryPageIndex = 0;
    lf010HistoryPageSize = this.lf010HistoryPageSizeOptions[0];

    readonly mapOptions = {
        layers: [
            tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}", {
                maxZoom: 18,
                attribution: "Esri ArcGIS",
            }),
        ],
        zoom: 17,
    };

    readonly boundsOptions = { padding: point(32, 32) };

    constructor() {}

    ngOnInit() {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.device) {
            this.resetLF010HistoryPagination();
            this.refreshMapPosition();
        }
    }

    onMapReady(map: LeafletMap): void {
        this.map = map;
        this.refreshMapPosition();
        setTimeout(() => this.refreshMapPosition(), 0);
        setTimeout(() => this.refreshMapPosition(), 150);
    }

    refreshMapPosition(): void {
        if (!this.map || !this.validCoordinates) {
            return;
        }

        this.map.invalidateSize({ pan: false });
        this.map.setView([this.latitude, this.longitude], this.zoom, { animate: false });
    }

    get validCoordinates() {
        return this.latitudeValid && this.longitudeValid;
    }

    get latitude() {
        return this.latitudeValid ? this.device?.location?.latitude : 0;
    }

    get longitude() {
        return this.longitudeValid ? this.device?.location?.longitude : 0;
    }

    get zoom() {
        return this.validCoordinates ? 17 : 0;
    }

    get mapLayers() {
        if (!this.validCoordinates) return [];
        return [marker([this.latitude, this.longitude], { icon: this.locationMarkerIcon })];
    }

    get mapBounds() {
        if (!this.validCoordinates) return null;
        const ll = latLng(this.latitude, this.longitude);
        return latLngBounds(ll, ll);
    }

    get showPicture() {
        return (
            (this.device?.picture && this.device.picture.length !== 0) ||
            (this.device?.baseDeviceData?.picture && this.device.baseDeviceData.picture.length !== 0)
        );
    }

    get picture() {
        if (this.device?.picture && this.device.picture.length !== 0) return this.device.picture;
        else return this.device?.baseDeviceData?.picture;
    }

    get pictureCloudinaryImage(): CloudinaryImage | null {
        return this.getCloudinaryImage(this.picture, 800, 600, "limit");
    }

    get showLF010Periods() {
        return this.isLF010Device && this.lf010Periods.length > 0;
    }

    get showLF010LimitHistory() {
        return this.isLF010Device;
    }

    get showLF010Section() {
        return this.showLF010Periods || this.showLF010LimitHistory;
    }

    get lf010Periods() {
        const devicePeriods = this.device?.settings?.lf010?.devicePeriods?.length
            ? this.device.settings.lf010.devicePeriods
            : this.defaultLF010DevicePeriods;
        const mappingByDevicePeriod = new Map(
            (this.device?.settings?.lf010?.periodMappings ?? []).map((mapping) => [mapping.devicePeriod, mapping.projectPeriodName]),
        );

        return devicePeriods.map((devicePeriod) => ({
            ...devicePeriod,
            projectPeriodName: mappingByDevicePeriod.get(devicePeriod.devicePeriod) ?? null,
        }));
    }

    get lf010LimitHistory() {
        return [...(this.device?.settings?.lf010?.limitHistory ?? [])].sort((left, right) => {
            const leftValue = left?.changedAt ? new Date(left.changedAt).getTime() : 0;
            const rightValue = right?.changedAt ? new Date(right.changedAt).getTime() : 0;
            return rightValue - leftValue;
        });
    }

    get paginatedLF010LimitHistory() {
        const start = this.lf010HistoryPageIndex * this.lf010HistoryPageSize;
        return this.lf010LimitHistory.slice(start, start + this.lf010HistoryPageSize);
    }

    onLF010HistoryPageChange(event: PageEvent): void {
        this.lf010HistoryPageIndex = event.pageIndex;
        this.lf010HistoryPageSize = event.pageSize;
    }

    private resetLF010HistoryPagination(): void {
        this.lf010HistoryPageIndex = 0;
        this.lf010HistoryPageSize = this.lf010HistoryPageSizeOptions[0];
    }

    private get latitudeValid() {
        return this.device?.location?.latitude && this.device.location.latitude >= -90 && this.device.location.latitude <= 90;
    }

    private get longitudeValid() {
        return this.device?.location?.longitude && this.device.location.longitude >= -90 && this.device.location.longitude <= 90;
    }

    private get isLF010Device() {
        return this.device?.baseDeviceData?.model?.toUpperCase() === "LF010";
    }

    public getCloudinaryImage(
        publicId: string | null | undefined,
        width: number | null = null,
        height: number | null = null,
        crop: "fill" | "limit" = "limit",
    ): CloudinaryImage | null {
        const id = (publicId || "").trim();
        if (!id || id.startsWith("http://") || id.startsWith("https://") || id.startsWith("data:")) {
            return null;
        }

        const key = `${id}|${width ?? ""}|${height ?? ""}|${crop}`;
        const cached = this.cloudinaryImageCache.get(key);
        if (cached) {
            return cached;
        }

        const image = this.cloudinary.image(id).format("auto").quality("auto");

        if (width !== null || height !== null) {
            const resizeAction = crop === "fill" ? fill() : limitFit();
            if (width !== null) {
                resizeAction.width(width);
            }
            if (height !== null) {
                resizeAction.height(height);
            }
            image.resize(resizeAction);
        }

        this.cloudinaryImageCache.set(key, image);
        return image;
    }
}
